import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

// Indian state/UT code → full name lookup
const STATE_NAMES = {
    AN: 'Andaman & Nicobar Islands', AP: 'Andhra Pradesh', AR: 'Arunachal Pradesh',
    AS: 'Assam', BR: 'Bihar', CH: 'Chandigarh', CG: 'Chhattisgarh',
    DN: 'Dadra & Nagar Haveli and Daman & Diu', DD: 'Daman & Diu', DL: 'Delhi',
    GA: 'Goa', GJ: 'Gujarat', HR: 'Haryana', HP: 'Himachal Pradesh',
    JK: 'Jammu & Kashmir', JH: 'Jharkhand', KA: 'Karnataka', KL: 'Kerala',
    KR: 'Karnataka', LA: 'Ladakh', LD: 'Lakshadweep', MP: 'Madhya Pradesh',
    MH: 'Maharashtra', MN: 'Manipur', ML: 'Meghalaya', MZ: 'Mizoram',
    NL: 'Nagaland', OD: 'Odisha', OR: 'Odisha', PY: 'Puducherry', PB: 'Punjab',
    RJ: 'Rajasthan', SK: 'Sikkim', TN: 'Tamil Nadu', TS: 'Telangana',
    TG: 'Telangana', TR: 'Tripura', UP: 'Uttar Pradesh', UK: 'Uttarakhand',
    UT: 'Uttarakhand', WB: 'West Bengal',
};

const resolveStateLabel = (code, nameFromApi) => {
    if (nameFromApi && nameFromApi !== code) return `${nameFromApi} (${code})`;
    const mapped = STATE_NAMES[String(code).toUpperCase()];
    return mapped ? `${mapped} (${code})` : code;
};

// Extract state list from various possible Surepass response shapes
const extractStates = (responseData) => {
    const d = responseData?.data?.data?.data
        ?? responseData?.data?.data
        ?? responseData?.data
        ?? responseData;
    if (!d) return [];

    const toOption = (s) => {
        if (typeof s === 'object') {
            const code = s.state_code || s.code || '';
            return { label: resolveStateLabel(code, s.state_name), value: code };
        }
        return { label: resolveStateLabel(s, null), value: s };
    };

    if (Array.isArray(d.state_code_list)) return d.state_code_list.map(toOption).filter(o => o.value);
    if (Array.isArray(d.states))          return d.states.map(toOption).filter(o => o.value);
    if (Array.isArray(d))                 return d.map(toOption).filter(o => o.value);

    // Fallback: collect any array-valued key
    const arrayEntry = Object.values(d).find((v) => Array.isArray(v) && v.length > 0);
    if (arrayEntry) return arrayEntry.map(toOption).filter(o => o.value);
    return [];
};

const SeFlowComponent = () => {
    const { stateListServiceId, detailsServiceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [states, setStates] = useState([]);
    // Starts false: nothing is fetched until the user opens the dropdown.
    const [loadingStates, setLoadingStates] = useState(false);
    const [stateLoadError, setStateLoadError] = useState(false);

    const [idNumber, setIdNumber] = useState('');
    const [stateCode, setStateCode] = useState(null);
    const [stateText, setStateText] = useState('');
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Load SE Details service credits
    useEffect(() => {
        if (!detailsServiceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${detailsServiceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [detailsServiceId]);

    // The state list is a BILLED call (2 credits). It used to run on mount, so
    // every page load — and every refresh — spent credits before the user had
    // done anything, and before the consent declaration was even on screen
    // (#112). It now loads on demand, once, when the user opens the dropdown.
    const statesRequestedRef = useRef(false);

    const loadStates = useCallback(() => {
        if (statesRequestedRef.current) return;      // one billed call per mount
        if (!stateListServiceId) { setStateLoadError(true); return; }

        statesRequestedRef.current = true;
        setLoadingStates(true);
        axiosInstance.get('/surepass/se/state-code-list', {
            params: { api_provider_service_id: parseInt(stateListServiceId, 10) },
        })
            .then((r) => {
                const extracted = extractStates(r.data);
                if (extracted.length > 0) {
                    setStates(extracted);
                } else {
                    setStateLoadError(true);
                }
            })
            .catch(() => {
                setStateLoadError(true);
                // Allow a retry: the credits were spent, but leaving the flag
                // set would strand the user with an empty, unrecoverable list.
                statesRequestedRef.current = false;
            })
            .finally(() => setLoadingStates(false));
    }, [stateListServiceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const rd = error.response?.data;
        let detail;
        if (s === 402) detail = `Insufficient credits. Required: ${rd?.required_credits ?? 'N/A'}, Available: ${rd?.current_balance ?? 'N/A'}`;
        else if (s === 422) { const e = rd?.errors; detail = e ? Object.values(e)[0]?.[0] : rd?.message || 'Validation failed'; }
        else if (s === 429) detail = 'Too many requests. Please wait before trying again.';
        else detail = rd?.message || error.message || summary;
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    const validate = () => {
        const next = {};
        if (!idNumber.trim()) next.idNumber = 'SE ID number is required';
        const selectedState = stateCode || stateText.trim();
        if (!selectedState) next.stateCode = 'State is required';
        if (!consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doSubmit = async () => {
        setLoading(true);
        const selectedState = stateCode || stateText.trim();
        try {
            const res = await axiosInstance.post('/surepass/se/details', {
                id_number: idNumber.trim(),
                state_code: selectedState,
                api_provider_service_id: parseInt(detailsServiceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Fetch failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'SE details fetched', detail: 'Report ready below', life: 2000 });
            try {
                const refreshed = await axiosInstance.get(`/user/report-services/${detailsServiceId}`);
                if (refreshed.data.success && refreshed.data.data) setServiceDetails(refreshed.data.data);
            } catch (_) {}
        } catch (err) {
            showError(err, 'SE details fetch failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        const selectedState = stateCode || stateText.trim();
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">SE ID:</span> <span className="text-900 font-medium font-mono">{idNumber}</span></div>
                    <div className="mb-2"><span className="text-600">State:</span> <span className="text-900 font-medium">{selectedState}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm SE details fetch',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: doSubmit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const reset = () => {
        setReportData(null);
        setIdNumber('');
        setStateCode(null);
        setStateText('');
        setConsent(false);
        setErrors({});
        submittingRef.current = false;
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-shop"
                        title="Shops & Establishment Details"
                        subtitle="Fetch SE registration details by ID and state"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-5">
                                        <label htmlFor="seId" className="block font-medium text-sm mb-2">
                                            SE ID Number <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-hashtag" />
                                            <InputText
                                                id="seId"
                                                value={idNumber}
                                                onChange={(e) => { setIdNumber(e.target.value || ''); if (errors.idNumber) setErrors((p) => ({ ...p, idNumber: '' })); }}
                                                placeholder="SE registration ID"
                                                maxLength={30}
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.idNumber })}
                                            />
                                        </IconField>
                                        {errors.idNumber && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.idNumber}</small>}
                                    </div>

                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="stateCode" className="block font-medium text-sm mb-2">
                                            State <span className="text-red-500">*</span>
                                        </label>
                                        {loadingStates ? (
                                            <div className="flex align-items-center gap-2 p-2">
                                                <i className="pi pi-spinner pi-spin text-primary" />
                                                <span className="text-500 text-sm">Loading states...</span>
                                            </div>
                                        ) : stateLoadError ? (
                                            // Lookup failed — fall back to typing the code by hand.
                                            <IconField iconPosition="left" className="w-full">
                                                <InputIcon className="pi pi-map-marker" />
                                                <InputText
                                                    id="stateCode"
                                                    value={stateText}
                                                    onChange={(e) => { setStateText((e.target.value || '').toUpperCase()); if (errors.stateCode) setErrors((p) => ({ ...p, stateCode: '' })); }}
                                                    placeholder="e.g. MH"
                                                    maxLength={5}
                                                    disabled={loading}
                                                    className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.stateCode })}
                                                />
                                            </IconField>
                                        ) : (
                                            // The option list costs credits to fetch, so it is loaded
                                            // when the user opens this dropdown rather than on mount.
                                            <Dropdown
                                                id="stateCode"
                                                value={stateCode}
                                                options={states}
                                                onShow={loadStates}
                                                onChange={(e) => { setStateCode(e.value); if (errors.stateCode) setErrors((p) => ({ ...p, stateCode: '' })); }}
                                                placeholder={states.length ? 'Select a state' : 'Click to load states'}
                                                emptyMessage="Loading states…"
                                                filter
                                                filterPlaceholder="Search state"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.stateCode })}
                                            />
                                        )}
                                        {stateLoadError && !loadingStates && <small className="text-orange-500 block mt-1"><i className="pi pi-exclamation-triangle mr-1" />Could not load states — enter manually</small>}
                                        {errors.stateCode && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.stateCode}</small>}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={consent}
                                    onChange={(e) => { setConsent(e.checked); if (errors.consent) setErrors((p) => ({ ...p, consent: '' })); }}
                                    error={errors.consent}
                                    reportType="se-details"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Fetch SE Details" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading || loadingStates} size="small" />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="Fetch Another" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={reset} />
                            </div>
                            <GenericSurepassOutputComponent reportData={reportData} title="Shops & Establishment Details" icon="pi-shop" />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SeFlowComponent;
